import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { Box, Typography, Chip } from '@material-ui/core';
import type { GroupedDimension } from '../../../../_models/schedule.model';
import { getStatusColors, getChartTheme, useThemeVersion } from '../../../../theme/chartTheme';

interface InstructorUtilizationChartProps {
  groupedByInstructor: GroupedDimension[];
}

const InstructorUtilizationChart: React.FC<InstructorUtilizationChartProps> = ({ groupedByInstructor }) => {
  const [sortBy, setSortBy] = useState<'hours' | 'sessions' | 'alphabetical'>('hours');
  const themeVersion = useThemeVersion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statusColors = React.useMemo(() => getStatusColors(), [themeVersion]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const chartGrid = React.useMemo(() => getChartTheme().grid, [themeVersion]);
  const [viewType, setViewType] = useState<'top' | 'bottom'>('top');
  const [showCount, setShowCount] = useState(10);

  const sortedData = [...groupedByInstructor]
    .map((instructor) => {
      const totalHours = instructor.slots.reduce((acc, slot) => {
        const start = slot.startTime ? new Date(`2000-01-01T${slot.startTime}`) : null;
        const end = slot.endTime ? new Date(`2000-01-01T${slot.endTime}`) : null;
        if (start && end) {
          const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
          return acc + diff;
        }
        return acc;
      }, 0);

      return {
        name: instructor.name.length > 20 ? instructor.name.substring(0, 20) + '...' : instructor.name,
        fullName: instructor.name,
        sessions: instructor.count,
        hours: parseFloat(totalHours.toFixed(1)),
      };
    })
    .sort((a, b) => {
      if (sortBy === 'hours') return viewType === 'top' ? b.hours - a.hours : a.hours - b.hours;
      if (sortBy === 'sessions') return viewType === 'top' ? b.sessions - a.sessions : a.sessions - b.sessions;
      return a.fullName.localeCompare(b.fullName);
    })
    .slice(0, showCount);

  const getWorkloadColor = (hours: number, maxHours: number) => {
    const percentage = (hours / maxHours) * 100;
    if (percentage >= 80) return statusColors.danger; // Overloaded
    if (percentage >= 60) return statusColors.warning; // Heavy load
    if (percentage >= 40) return statusColors.success; // Balanced
    return statusColors.info; // Light load
  };

  const maxHours = Math.max(...sortedData.map((d) => d.hours), 1);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const avgHoursPerSession = data.sessions > 0 ? (data.hours / data.sessions).toFixed(1) : '0';
      return (
        <div
          style={{
            background: 'white',
            padding: '12px 16px',
            border: '1px solid var(--border-default)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4 }}>
            {data.fullName}
          </Typography>
          <Typography variant="body2" style={{ color: 'var(--text-muted)', marginBottom: 2 }}>
            Total Hours: {data.hours}h
          </Typography>
          <Typography variant="body2" style={{ color: 'var(--text-muted)', marginBottom: 2 }}>
            Sessions: {data.sessions}
          </Typography>
          <Typography variant="body2" style={{ color: 'var(--text-muted)' }}>
            Avg: {avgHoursPerSession}h per session
          </Typography>
        </div>
      );
    }
    return null;
  };

  if (groupedByInstructor.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <Typography variant="body1" style={{ color: 'var(--text-muted)' }}>
          No instructor data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" style={{ gap: '8px' }}>
        <Box display="flex" style={{ gap: '8px' }} flexWrap="wrap">
          <Chip
            label="Sort: Hours"
            onClick={() => setSortBy('hours')}
            style={{
              background: sortBy === 'hours' ? 'var(--brand-primary)' : 'var(--border-default)',
              color: sortBy === 'hours' ? 'white' : 'var(--text-muted)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          />
          <Chip
            label="Sort: Sessions"
            onClick={() => setSortBy('sessions')}
            style={{
              background: sortBy === 'sessions' ? 'var(--brand-primary)' : 'var(--border-default)',
              color: sortBy === 'sessions' ? 'white' : 'var(--text-muted)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          />
          <Chip
            label="Sort: A-Z"
            onClick={() => setSortBy('alphabetical')}
            style={{
              background: sortBy === 'alphabetical' ? 'var(--brand-primary)' : 'var(--border-default)',
              color: sortBy === 'alphabetical' ? 'white' : 'var(--text-muted)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          />
        </Box>
        <Box display="flex" flexDirection="column" style={{ gap: '8px' }}>
          <Box display="flex" alignItems="center" flexWrap="wrap">
            <Typography
              variant="caption"
              style={{ color: 'var(--text-muted)', fontWeight: 600, marginRight: '8px', minWidth: '110px' }}
            >
              Top Performers:
            </Typography>
            {[5, 10].map((num) => (
              <Chip
                key={`top-${num}`}
                label={`Top ${num}`}
                onClick={() => {
                  setViewType('top');
                  setShowCount(num);
                }}
                size="small"
                style={{
                  background: viewType === 'top' && showCount === num ? 'var(--status-success)' : 'var(--border-default)',
                  color: viewType === 'top' && showCount === num ? 'white' : 'var(--text-muted)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginRight: '4px',
                  marginBottom: '4px',
                }}
              />
            ))}
          </Box>
          <Box display="flex" alignItems="center" flexWrap="wrap">
            <Typography
              variant="caption"
              style={{ color: 'var(--status-danger)', fontWeight: 600, marginRight: '8px', minWidth: '110px' }}
            >
              Under-Utilized:
            </Typography>
            {[5, 10].map((num) => (
              <Chip
                key={`bottom-${num}`}
                label={`Bottom ${num}`}
                onClick={() => {
                  setViewType('bottom');
                  setShowCount(num);
                }}
                size="small"
                style={{
                  background: viewType === 'bottom' && showCount === num ? 'var(--status-danger)' : 'var(--border-default)',
                  color: viewType === 'bottom' && showCount === num ? 'white' : 'var(--text-muted)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginRight: '4px',
                  marginBottom: '4px',
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={sortedData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={100}
            style={{ fontSize: '12px', fill: 'var(--text-muted)' }}
          />
          <YAxis style={{ fontSize: '12px', fill: 'var(--text-muted)' }} label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="hours" radius={[8, 8, 0, 0]}>
            {sortedData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getWorkloadColor(entry.hours, maxHours)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Box mt={2} display="flex" justifyContent="center" style={{ gap: '24px' }} flexWrap="wrap">
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', background: 'var(--status-info)', borderRadius: '4px' }} />
          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
            Light Load (&lt;40%)
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', background: 'var(--status-success)', borderRadius: '4px' }} />
          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
            Balanced (40-60%)
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', background: 'var(--status-warning)', borderRadius: '4px' }} />
          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
            Heavy (60-80%)
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', background: 'var(--status-danger)', borderRadius: '4px' }} />
          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
            Overloaded (≥80%)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default InstructorUtilizationChart;
